'use client';

import { useEffect, useState } from 'react';

import { usePathname } from 'next/navigation';

import {
  ArrowLeft,
  ArrowRight,
  Bot,
  Brain,
  Briefcase,
  Building2,
  ChartLine,
  ClipboardCheck,
  Compass,
  Eye,
  GitBranch,
  Globe,
  Hash,
  Headset,
  Heart,
  Mail,
  Menu,
  MessageCircle,
  Phone,
  Plane,
  Puzzle,
  Radio,
  Rocket,
  SearchCheck,
  Share2,
  Sparkles,
  ShieldCheck,
  Users,
  Workflow,
  X,
  Zap,
} from 'lucide-react';

import { LocaleSwitch } from '@/components/layout/locale-switch';
import { Button } from '@/components/ui/button';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from '@/components/ui/navigation-menu';
import { useAnnouncement } from '@/hooks/use-announcement';
import { isRTL } from '@/lib/i18n';
import { useLocale, useTranslation } from '@/lib/i18n/context';
import { Link } from '@/lib/i18n/link';
import { cn } from '@/lib/utils';

type ProductItem = {
  title: string;
  desc: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
};

/**
 * Locale-keyed content map for nav strings that aren't in the shared `t.nav`
 * dictionary (product descriptions, company/industry copy, customer-story
 * items, the $2M featured card). Keeps English identical for /en while giving
 * /ar native Saudi (sawt.sa-style) copy. Brand names, "$2M", hrefs stay as-is.
 */
const NAV_AR: Record<string, string> = {
  // Channel descriptions
  'Phone support that picks up': 'دعم هاتفي يرد فعلاً',
  'Chat that actually resolves': 'دردشة تحلّ، مو بس ترد',
  'Inbox automation, end-to-end': 'أتمتة الإيميل من أوله لآخره',
  'Two-way messaging at scale': 'رسائل بالاتجاهين، وبأي حجم',
  'Internal & external support': 'دعم داخلي وخارجي',
  'X, Instagram, FB, TikTok': 'X وInstagram وFB وTikTok',
  // Helpdesk descriptions
  'All-in-one AI helpdesk': 'نظام دعم ذكي شامل',
  'The Intercom AI you wanted': 'ذكاء Intercom اللي تبيه',
  'Live in under an hour': 'جاهز في أقل من ساعة',
  'Any tier, every feature': 'أي باقة، وكل الميزات',
  'Real resolutions': 'حلول فعلية',
  'Service Cloud, no consultants': 'Service Cloud بدون استشاريين',
  // AI Engine descriptions
  'The AI brain': 'العقل المدبّر',
  'Every decision explained': 'كل قرار له تفسير',
  'Multi-step API calls': 'إجراءات متعددة عبر API',
  'SOC 2 & ISO 27001 certified': 'حاصل على شهادتي SOC 2 وISO 27001',
  'Quality tracked at scale': 'جودة مقيسة بأي حجم',
  // Platform descriptions
  'The complete AI phone system': 'نظام هاتف ذكي متكامل',
  'Multi-party orchestration': 'تنسيق بين أكثر من طرف',
  'AI personalization at scale': 'تخصيص ذكي بأي حجم',
  'Live VoC research': 'أبحاث صوت العميل لحظياً',
  'KB autopilot': 'قاعدة معرفة تكمّل نفسها',
  'Build features by prompting': 'ميزات تبنيها بالكلام',
  '100+ tool connectors': 'أكثر من ١٠٠ ربط جاهز',
  'Pre-built playbooks': 'قوالب جاهزة',
  'Live in production today': 'شغّالة اليوم عند عملائنا',
  'Launch AI support under your brand': 'أطلق دعماً ذكياً تحت علامتك',
  // Company items
  'About us': 'من نحن',
  'Learn about our mission and values.': 'تعرّف على رسالتنا وقيمنا.',
  Careers: 'الوظائف',
  'Join the team and build the future of customer communication.':
    'انضم للفريق وابنِ مستقبل التواصل مع العملاء.',
  'Success stories': 'قصص النجاح',
  'See how customers achieve their goals.': 'تعرّف كيف حقّق عملاؤنا أهدافهم.',
  // Industries items
  'Industries Overview': 'نظرة على القطاعات',
  'AI customer support across all sectors.': 'دعم عملاء ذكي لكل القطاعات.',
  'Financial Services': 'الخدمات المالية',
  'Compliant AI for sensitive inquiries.':
    'متوافق مع التنظيمات، للأسئلة الحسّاسة.',
  Healthcare: 'الرعاية الصحية',
  'HIPAA-ready AI for patient support.': 'متوافق مع HIPAA لدعم المرضى.',
  'Mobility & Transportation': 'التنقل والمواصلات',
  'Support for ride-sharing and car rentals.': 'مشاركة رحلات وتأجير سيارات.',
  Technology: 'التقنية',
  'Faster onboarding, less churn.': 'تفعيل أسرع، وتسرّب عملاء أقل.',
  'Tour Operators': 'منظّمو الرحلات',
  'Bookings and weather cancellations, 24/7.': 'حجوزات وإلغاءات الطقس، ٢٤/٧.',
  // Customer-story items (brand names preserved)
  'Mediating disputes with all-time high CSAT.':
    'حل النزاعات مع أعلى رضا عملاء على الإطلاق.',
  '55M customers, 200+ countries, 70% automation.':
    '٥٥ مليون عميل، أكثر من ٢٠٠ دولة، ٧٠٪ أتمتة.',
  '80% automation across 60K+ delivery reviews.':
    '٨٠٪ أتمتة عبر أكثر من ٦٠ ألف تقييم توصيل.',
  '77% automation with 90%+ CSAT score.': '٧٧٪ أتمتة مع رضا عملاء فوق ٩٠٪.',
  'Real companies. Real results.': 'شركات حقيقية. نتائج حقيقية.',
  // Featured $2M card
  '$2M Guarantee': 'ضمان استرجاع ٧,٥ مليون ⃁',
  'Money back. Up to $2M.': 'أموالك تعود. حتى ٧,٥ مليون ⃁.',
  'The boldest promise in support': 'أجرأ وعد في الدعم',
  "If Open isn't working for you in 120 days, we refund every dollar of Open spend — up to $2M back, no questions asked.":
    'إذا ما نفعك Open خلال ١٢٠ يوم، نرجّع لك كل ريال دفعته على Open — حتى ٧,٥ مليون ⃁، بدون أي أسئلة.',
  Featured: 'مميّز',
};

/** Returns the Arabic string for /ar, else the English original. */
const navText = (locale: string, en: string): string =>
  locale === 'ar' ? (NAV_AR[en] ?? en) : en;

const PRODUCT_CHANNELS: ProductItem[] = [
  {
    title: 'AI Voice',
    desc: 'Phone support that picks up',
    href: '/channels/voice',
    icon: Phone,
  },
  {
    title: 'AI Chat',
    desc: 'Chat that actually resolves',
    href: '/channels/chat',
    icon: MessageCircle,
  },
  {
    title: 'AI Email',
    desc: 'Inbox automation, end-to-end',
    href: '/channels/email',
    icon: Mail,
  },
  {
    title: 'AI WhatsApp',
    desc: 'Two-way messaging at scale',
    href: '/channels/whatsapp',
    icon: MessageCircle,
  },
  {
    title: 'AI Slack',
    desc: 'Internal & external support',
    href: '/channels/slack',
    icon: Hash,
  },
  {
    title: 'AI Social',
    desc: 'X, Instagram, FB, TikTok',
    href: '/channels/social',
    icon: Share2,
  },
];

const PRODUCT_HELPDESKS: ProductItem[] = [
  {
    title: 'Open',
    desc: 'All-in-one AI helpdesk',
    href: '/helpdesks/open',
    icon: Globe,
  },
  {
    title: 'Intercom',
    desc: 'The Intercom AI you wanted',
    href: '/helpdesks/intercom',
    icon: MessageCircle,
  },
  {
    title: 'Zendesk',
    desc: 'Live in under an hour',
    href: '/helpdesks/zendesk',
    icon: Headset,
  },
  {
    title: 'HubSpot',
    desc: 'Any tier, every feature',
    href: '/helpdesks/hubspot',
    icon: Workflow,
  },
  {
    title: 'Freshdesk',
    desc: 'Real resolutions',
    href: '/helpdesks/freshdesk',
    icon: Headset,
  },
  {
    title: 'Salesforce',
    desc: 'Service Cloud, no consultants',
    href: '/helpdesks/salesforce',
    icon: Zap,
  },
];

const PRODUCT_AI_ENGINE: ProductItem[] = [
  { title: 'Agent 5', desc: 'The AI brain', href: '/agent5', icon: Brain },
  {
    title: 'Observability',
    desc: 'Every decision explained',
    href: '/agent5#observability',
    icon: Eye,
  },
  {
    title: 'Actions',
    desc: 'Multi-step API calls',
    href: '/agent5#actions',
    icon: Zap,
  },
  {
    title: 'Compliance',
    desc: 'SOC 2 & ISO 27001 certified',
    href: '/agent5#compliance',
    icon: ShieldCheck,
  },
  {
    title: 'Evals',
    desc: 'Quality tracked at scale',
    href: '/agent5#evals',
    icon: ClipboardCheck,
  },
];

const PRODUCT_PLATFORM: ProductItem[] = [
  {
    title: 'Open Telephony',
    desc: 'The complete AI phone system',
    href: '/open-telephony',
    icon: Phone,
  },
  {
    title: 'Workflows',
    desc: 'Multi-party orchestration',
    href: '/workflows',
    icon: Bot,
  },
  {
    title: 'Outbound Sequencing',
    desc: 'AI personalization at scale',
    href: '/outbound-sequencing',
    icon: GitBranch,
  },
  {
    title: 'Customer Insights',
    desc: 'Live VoC research',
    href: '/customer-insights',
    icon: ChartLine,
  },
  {
    title: 'Knowledge Gap Detection',
    desc: 'KB autopilot',
    href: '/knowledge-gap-detection',
    icon: SearchCheck,
  },
  {
    title: 'App Builder',
    desc: 'Build features by prompting',
    href: '/app-builder',
    icon: Sparkles,
  },
  {
    title: 'Integrations',
    desc: '100+ tool connectors',
    href: '/integrations',
    icon: Puzzle,
  },
  {
    title: 'Templates',
    desc: 'Pre-built playbooks',
    href: '/templates',
    icon: Puzzle,
  },
  {
    title: 'Use Cases',
    desc: 'Live in production today',
    href: '/use-cases',
    icon: Radio,
  },
  {
    title: 'White-label',
    desc: 'Launch AI support under your brand',
    href: '/whitelabel',
    icon: Share2,
  },
];

const companyItems = [
  {
    id: 'company-1',
    title: 'About us',
    description: 'Learn about our mission and values.',
    href: '/about-open',
    icon: Users,
  },
  {
    id: 'company-2',
    title: 'Careers',
    description:
      'Join the team and build the future of customer communication.',
    href: 'https://careers.open.cx',
    icon: Briefcase,
  },
  {
    id: 'company-3',
    title: 'Success stories',
    description: 'See how customers achieve their goals.',
    href: '/customer-stories',
    icon: Rocket,
  },
];

const industries = [
  {
    id: 'industry-1',
    title: 'Industries Overview',
    description: 'AI customer support across all sectors.',
    href: '/industries',
    icon: Globe,
  },
  {
    id: 'industry-2',
    title: 'Financial Services',
    description: 'Compliant AI for sensitive inquiries.',
    href: '/industries/financial-services',
    icon: Building2,
  },
  {
    id: 'industry-3',
    title: 'Healthcare',
    description: 'HIPAA-ready AI for patient support.',
    href: '/industries/healthcare',
    icon: Heart,
  },
  {
    id: 'industry-4',
    title: 'Mobility & Transportation',
    description: 'Support for ride-sharing and car rentals.',
    href: '/industries/mobility',
    icon: Plane,
  },
  {
    id: 'industry-5',
    title: 'Technology',
    description: 'Faster onboarding, less churn.',
    href: '/industries/technology',
    icon: Zap,
  },
  {
    id: 'industry-6',
    title: 'Tour Operators',
    description: 'Bookings and weather cancellations, 24/7.',
    href: '/industries/tour-operators',
    icon: Compass,
  },
];

const ProductMenuItem = ({
  item,
  t,
  locale,
  onLinkClick,
}: {
  item: ProductItem;
  t: ReturnType<typeof useTranslation>['t'];
  locale: string;
  onLinkClick?: () => void;
}) => {
  return (
    <NavigationMenuLink asChild className="rounded-md p-0">
      <Link
        href={item.href}
        prefetch={true}
        {...(onLinkClick && { onClick: onLinkClick })}
        className="group flex min-w-0 flex-col gap-0.5 rounded-md px-2.5 py-1.5 text-start transition-colors hover:bg-neutral-100"
      >
        <span className="text-sm leading-tight font-medium text-neutral-900">
          {t.nav[item.title as keyof typeof t.nav] || item.title}
        </span>
        <span className="line-clamp-2 text-xs leading-snug text-neutral-500">
          {navText(locale, item.desc)}
        </span>
      </Link>
    </NavigationMenuLink>
  );
};

const SolutionsMenu = ({
  t,
  rtl,
  locale,
  onLinkClick,
}: {
  t: ReturnType<typeof useTranslation>['t'];
  rtl: boolean;
  locale: string;
  onLinkClick?: () => void;
}) => {
  const renderCategory = (title: string, items: ProductItem[]) => (
    <div>
      <p className="mb-2 px-2.5 text-[11px] font-medium tracking-widest text-neutral-500 uppercase">
        {t.nav[title as keyof typeof t.nav] || title}
      </p>
      <div className="flex flex-col gap-0.5">
        {items.map((item) => (
          <ProductMenuItem
            key={item.href}
            item={item}
            t={t}
            locale={locale}
            onLinkClick={onLinkClick}
          />
        ))}
      </div>
    </div>
  );

  return (
    <div
      className={`grid gap-6 lg:grid-cols-[minmax(0,1fr)_260px] ${rtl ? 'direction-rtl' : ''}`}
    >
      <div className="flex flex-col gap-6">
        {/* Top three categories */}
        {/* border-s, not divide-x: logical properties put the rule on the
            correct side once the menu flips to RTL. */}
        <div className="grid gap-x-6 gap-y-6 md:grid-cols-3 md:[&>*+*]:border-s md:[&>*+*]:border-neutral-200 md:[&>*+*]:ps-6">
          {renderCategory('Channels', PRODUCT_CHANNELS)}
          {renderCategory('Helpdesks', PRODUCT_HELPDESKS)}
          {renderCategory('The AI Engine', PRODUCT_AI_ENGINE)}
        </div>

        {/* Divider + Platform row */}
        <div className="border-t border-neutral-200 pt-4">
          <p className="mb-2 px-2.5 text-[11px] font-medium tracking-widest text-neutral-500 uppercase">
            {t.nav['Platform' as keyof typeof t.nav] || 'Platform'}
          </p>
          <div className="grid gap-x-6 gap-y-0.5 md:grid-cols-3">
            {PRODUCT_PLATFORM.map((item) => (
              <ProductMenuItem
                key={item.href}
                item={item}
                t={t}
                locale={locale}
                onLinkClick={onLinkClick}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Featured panel */}
      <div className="hidden flex-col gap-3 rounded-2xl bg-neutral-50 p-4 lg:flex">
        <p className="text-[11px] font-medium tracking-widest text-neutral-500 uppercase">
          {t.nav['Featured' as keyof typeof t.nav] || 'Featured'}
        </p>
        <NavigationMenuLink
          asChild
          className="block rounded-xl p-0 hover:text-white focus:text-white"
        >
          <Link
            href="/guarantee"
            prefetch={true}
            {...(onLinkClick && { onClick: onLinkClick })}
            className="group block overflow-hidden rounded-xl bg-neutral-900 p-4 text-white transition-colors hover:bg-neutral-800"
          >
            <p className="text-[10px] font-semibold tracking-widest text-amber-400 uppercase">
              {navText(locale, '$2M Guarantee')}
            </p>
            <h3 className="mt-2 font-serif text-lg">
              {navText(locale, 'Money back. Up to $2M.')}
            </h3>
          </Link>
        </NavigationMenuLink>
        <div className="px-1">
          <p className="text-sm font-medium text-neutral-900">
            {navText(locale, 'The boldest promise in support')}
          </p>
          <p className="mt-1 text-xs leading-snug text-neutral-500">
            {navText(
              locale,
              "If Open isn't working for you in 120 days, we refund every dollar of Open spend — up to $2M back, no questions asked.",
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

type MenuItemConfig = {
  title: string;
  description: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
};

const MenuItem = ({
  item,
  t,
  locale,
  onLinkClick,
}: {
  item: MenuItemConfig;
  t: ReturnType<typeof useTranslation>['t'];
  locale: string;
  onLinkClick?: () => void;
}) => {
  return (
    <NavigationMenuLink asChild className="rounded-md p-0">
      <Link
        href={item.href}
        prefetch={true}
        {...(onLinkClick && { onClick: onLinkClick })}
        className="group flex min-w-0 flex-col gap-0.5 rounded-md px-2.5 py-1.5 text-start transition-colors hover:bg-neutral-100"
      >
        <span className="text-sm leading-tight font-medium text-neutral-900">
          {t.nav[item.title as keyof typeof t.nav] ||
            navText(locale, item.title)}
        </span>
        <span className="line-clamp-2 text-xs leading-snug text-neutral-500">
          {t.nav[item.description as keyof typeof t.nav] ||
            navText(locale, item.description)}
        </span>
      </Link>
    </NavigationMenuLink>
  );
};

const GlobalGuidanceMenu = ({
  t,
  locale,
  onLinkClick,
}: {
  t: ReturnType<typeof useTranslation>['t'];
  rtl: boolean;
  locale: string;
  onLinkClick?: () => void;
}) => {
  return (
    <div className="flex flex-col gap-1">
      {companyItems.map((item) => (
        <MenuItem
          key={item.id}
          item={item}
          t={t}
          locale={locale}
          onLinkClick={onLinkClick}
        />
      ))}
    </div>
  );
};

const CustomersMenu = ({
  t,
  rtl,
  locale,
  onLinkClick,
}: {
  t: ReturnType<typeof useTranslation>['t'];
  rtl: boolean;
  locale: string;
  onLinkClick?: () => void;
}) => {
  const Arrow = rtl ? ArrowLeft : ArrowRight;
  const arrowHover = rtl
    ? 'group-hover:-translate-x-1'
    : 'group-hover:translate-x-1';

  const customerStoryItems: MenuItemConfig[] = [
    {
      title: t.nav['TicketSwap - 19M Users'] || 'TicketSwap',
      description:
        t.nav['Mediating disputes with all-time high CSAT.'] ||
        navText(locale, 'Mediating disputes with all-time high CSAT.'),
      href: '/customer-stories/ticketswap',
      icon: Rocket,
    },
    {
      title: t.nav['MoneyGram'] || 'MoneyGram',
      description:
        t.nav['55M customers, 200+ countries, 70% automation.'] ||
        navText(locale, '55M customers, 200+ countries, 70% automation.'),
      href: '/customer-stories/moneygram',
      icon: Rocket,
    },
    {
      title: 'Trunkrs',
      description: navText(
        locale,
        '80% automation across 60K+ delivery reviews.',
      ),
      href: '/customer-stories/trunkrs',
      icon: Rocket,
    },
    {
      title: 'OTO',
      description: navText(locale, '77% automation with 90%+ CSAT score.'),
      href: '/customer-stories/oto',
      icon: Rocket,
    },
    {
      title: t.nav['All Customer Stories'] || 'All customer stories',
      description:
        t.nav['Real companies. Real results.'] ||
        navText(locale, 'Real companies. Real results.'),
      href: '/customer-stories',
      icon: Users,
    },
  ];

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
      <div className="grid gap-1 md:grid-cols-2 md:gap-x-6">
        {customerStoryItems.map((item) => (
          <NavigationMenuLink
            key={item.href}
            asChild
            className="flex-row items-center gap-3 rounded-md p-0"
          >
            <Link
              href={item.href}
              prefetch={true}
              {...(onLinkClick && { onClick: onLinkClick })}
              className="group flex min-w-0 flex-row items-center gap-3 rounded-md px-2.5 py-2 text-start transition-colors hover:bg-neutral-100"
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-neutral-100 text-neutral-600 transition-colors group-hover:bg-neutral-200 group-hover:text-neutral-900">
                <item.icon className="h-4 w-4" />
              </span>
              <span className="flex min-w-0 flex-col">
                <span className="truncate text-sm font-medium leading-tight text-neutral-900">
                  {item.title}
                </span>
                <span className="mt-0.5 truncate text-xs text-neutral-500">
                  {item.description}
                </span>
              </span>
            </Link>
          </NavigationMenuLink>
        ))}
      </div>

      {/* Featured Mollie card */}
      <NavigationMenuLink
        asChild
        className="hidden rounded-2xl p-0 hover:text-white focus:text-white lg:block"
      >
        <Link
          href="/customer-stories/mollie"
          prefetch={true}
          className="group relative hidden h-44 overflow-hidden rounded-2xl bg-black p-5 text-white lg:flex"
          {...(onLinkClick && { onClick: onLinkClick })}
        >
          <img
            src="https://framerusercontent.com/images/nXlKoMs3pGPM1AgcMBUGZmucp0.png?width=1260&height=780"
            alt="Mollie"
            className="absolute inset-0 h-full w-full object-cover opacity-40 transition-transform duration-300 group-hover:scale-[1.02]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent" />
          <div className="relative z-10 mt-auto flex w-full flex-col text-start">
            <p className="text-[10px] font-medium uppercase tracking-widest text-amber-400">
              {navText(locale, 'Featured')}
            </p>
            <div className="mt-2 flex items-center gap-1 text-sm font-medium">
              {t.nav['Mollie - €6.5B Financial Services']}
              <Arrow className={`size-4 transition-transform ${arrowHover}`} />
            </div>
            <p className="mt-1 text-xs leading-snug text-neutral-300">
              {
                t.nav[
                  'How a €6.5B company supports 250,000+ businesses with 60%+ automation across 8 languages.'
                ]
              }
            </p>
          </div>
        </Link>
      </NavigationMenuLink>
    </div>
  );
};

const IndustriesMenu = ({
  t,
  locale,
  onLinkClick,
}: {
  t: ReturnType<typeof useTranslation>['t'];
  rtl: boolean;
  locale: string;
  onLinkClick?: () => void;
}) => {
  return (
    <div className="grid gap-x-6 gap-y-1 sm:grid-cols-2">
      {industries.map((industry) => (
        <MenuItem
          key={industry.id}
          item={industry}
          t={t}
          locale={locale}
          onLinkClick={onLinkClick}
        />
      ))}
    </div>
  );
};

interface NavbarProps {
  variant?: 'dark' | 'light';
}

const Navbar = ({ variant = 'dark' }: NavbarProps) => {
  const pathname = usePathname();
  const locale = useLocale();
  const { t } = useTranslation();
  const rtl = isRTL(locale);

  const [open, setOpen] = useState(false);
  const [submenu, setSubmenu] = useState<
    | 'solutions'
    | 'products'
    | 'global'
    | 'customers'
    | 'industries'
    | 'pricing'
    | null
  >(null);
  const [menuOpen, setMenuOpen] = useState(false);
  // Seeded from the real scroll offset rather than a flat `false`. The state
  // only ever corrected itself on a scroll *event*, so any load that starts
  // partway down the page — scroll restoration on refresh, a back navigation,
  // an anchor deep-link — left the nav in its top-of-page state: announcement
  // bar open, chrome transparent, and page content showing through the logo.
  const [isScrolled, setIsScrolled] = useState(
    () => typeof window !== 'undefined' && window.scrollY > 50,
  );
  /** True when the navbar is currently overlapping a section marked with
   * `data-nav-theme="dark"`. Used to flip the nav to dark + blur. */
  const [isOverDark, setIsOverDark] = useState(false);
  const {
    isClosed: announcementClosed,
    isReady: announcementReady,
    close: closeAnnouncement,
  } = useAnnouncement();

  const Arrow = rtl ? ArrowLeft : ArrowRight;

  const handleCloseMenu = () => {
    setOpen(false);
    setSubmenu(null);
  };

  type NavMenuItem =
    | {
        key: 'solutions' | 'global' | 'customers' | 'industries';
        label: string;
        component: (onLinkClick?: () => void) => React.ReactNode;
        /** Width and placement of the dropped panel. Each menu is sized to
         *  its own content — a three-item list does not need the whole
         *  viewport. */
        contentClassName: string;
        /** `static` releases the item as the panel's positioning context, so
         *  the panel centres on the nav row (i.e. the page) instead. */
        itemClassName?: string;
        href?: never;
      }
    | {
        key: 'pricing';
        label: string;
        href: string;
        component?: never;
        contentClassName?: never;
        itemClassName?: never;
      };

  // Pricing sits last: it is the only item that just navigates, so grouping it
  // after the three that drop panels keeps the menu-bearing triggers together.
  const navigationMenuItems: NavMenuItem[] = [
    {
      key: 'solutions',
      label: t.nav['Product'],
      // Too wide to sit under its trigger without hanging off the page, so
      // this one centres on the nav row instead.
      contentClassName:
        'w-[min(calc(100vw-4rem),1080px)] left-1/2 -translate-x-1/2',
      itemClassName: 'static',
      component: (onLinkClick?: () => void) => (
        <SolutionsMenu
          t={t}
          rtl={rtl}
          locale={locale}
          onLinkClick={onLinkClick}
        />
      ),
    },
    {
      key: 'industries',
      label: t.nav['Industries'],
      contentClassName:
        'w-[min(calc(100vw-4rem),520px)] left-1/2 -translate-x-1/2',
      component: (onLinkClick?: () => void) => (
        <IndustriesMenu
          t={t}
          rtl={rtl}
          locale={locale}
          onLinkClick={onLinkClick}
        />
      ),
    },
    {
      key: 'global',
      label: t.nav['Company'],
      contentClassName:
        'w-[min(calc(100vw-4rem),300px)] left-1/2 -translate-x-1/2',
      component: (onLinkClick?: () => void) => (
        <GlobalGuidanceMenu
          t={t}
          rtl={rtl}
          locale={locale}
          onLinkClick={onLinkClick}
        />
      ),
    },
    {
      key: 'pricing',
      label: t.nav['Pricing'],
      href: '/pricing',
    },
  ];

  // Check if on homepage (/ or /en, /ar, etc.)
  const isHomepage = pathname === '/' || /^\/[a-z]{2}$/.test(pathname);

  // Force dark variant on specific pages, but light on homepage
  const isPresentations = pathname.includes('/presentations/');
  const effectiveVariant =
    pathname === '/open-academy' || isPresentations
      ? 'dark'
      : isHomepage
        ? 'light'
        : variant;
  const isDark = effectiveVariant === 'dark';

  // Use dark styling on homepage (light background hero image)
  const useWhiteNav = false; // Disabled since hero now has white background

  /* The customer-stories index is dark end to end — a film that fills the
   * viewport, then a near-black wall — so the chrome floats over the whole
   * page rather than capping it with a bar at any point. This is its own flag
   * rather than `data-nav-theme="dark"` because that path forces `bg-black`
   * (see `showBackdrop` below): correct for a dark *section* inside a light
   * page, wrong for a page that is dark the whole way down. Only the index; a
   * story page underneath it is an ordinary light page. */
  const isDarkStoriesPage = /^(\/[a-z]{2})?\/customer-stories\/?$/.test(
    pathname,
  );

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    // Measure once on mount as well: a restored scroll position fires no
    // scroll event, and the lazy initializer above does not run on the server.
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Detect when the navbar is currently sitting over a section marked with
  // `data-nav-theme="dark"` and flip the nav chrome to dark + blur.
  useEffect(() => {
    let frame = 0;

    const measure = () => {
      const els = document.querySelectorAll<HTMLElement>(
        '[data-nav-theme="dark"]',
      );
      if (els.length === 0) {
        setIsOverDark(false);
        return;
      }
      // The navbar height ~ 68px (`--header-height`). Treat the top 80px of
      // the viewport as the navbar zone; if any dark section overlaps it, flip.
      const navZoneBottom = 80;
      let overlaps = false;
      els.forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.top <= navZoneBottom && rect.bottom >= 0) overlaps = true;
      });
      setIsOverDark(overlaps);
    };

    const onScroll = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };

    measure();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [pathname]);

  // Floating for the length of the page, scrolled or not.
  const overFilmHero = isDarkStoriesPage;

  // On homepage, always use white text (even when scrolled)
  const useWhiteText = useWhiteNav || overFilmHero;

  // Combined: dark chrome when either the page's nav variant is dark or the
  // navbar is currently overlapping a section marked `data-nav-theme="dark"`.
  const useDarkChrome = isDark || isOverDark;
  // When over a dark section we always want a visible blurred bg, even at the
  // very top of the page (before the 50px scroll threshold).
  const showBackdrop = isScrolled || isOverDark;

  const navbarBackground = cn(
    // Solid, full-color chrome — no translucency, no backdrop blur, no bottom
    // border.
    overFilmHero
      ? 'bg-transparent'
      : useDarkChrome || useWhiteNav
        ? // Dark chrome may still float over its own hero at the top of the
          // page. That is safe: `isOverDark` measures on mount rather than
          // waiting for a scroll event, so it is never stale the way
          // `isScrolled` used to be.
          showBackdrop
          ? 'bg-black'
          : 'bg-transparent'
        : // Light chrome is never transparent. #fafaf9 is the page background
          // the nav sits on at the top anyway, so painting it unconditionally
          // is invisible there — and it means no scroll-state bug can put page
          // content behind the logo again.
          'bg-[#fafaf9]',
  );

  return (
    <>
      {/* Dark overlay when menu is open */}
      <div
        // transition-all here animated backdrop-filter across the whole
        // viewport on every menu open, which re-blurs the entire page each
        // frame. The blur is constant now and only opacity moves, which the
        // compositor can do without repainting anything.
        className={cn(
          'pointer-events-none fixed inset-0 z-40 bg-black/40 transition-opacity duration-150',
          menuOpen
            ? 'opacity-100 backdrop-blur-sm'
            : 'opacity-0 backdrop-blur-none',
        )}
        // style={{ top: '65px' }}
      />

      <section
        dir={rtl ? 'rtl' : 'ltr'}
        suppressHydrationWarning
        className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${navbarBackground}`}
      >
        {/* Funding Round Announcement */}
        <div
          className={cn(
            'radius-12 relative w-full overflow-hidden',
            // Over the customer-stories film the whole bar floats: a light
            // strip capping a full-bleed hero is the one thing that makes it
            // stop reading as full-bleed.
            overFilmHero
              ? 'bg-transparent text-white/80'
              : 'bg-[#f4f3f2] text-[#78716c]',
            announcementReady && 'transition-all duration-300 ease-in-out',
            !announcementReady || announcementClosed || isScrolled
              ? 'h-0 py-0 opacity-0'
              : 'h-(--announcement-bar-height) py-2.5 opacity-100',
          )}
        >
          <div
            className={`relative container flex items-center justify-center gap-2 text-center text-sm font-normal ${rtl ? 'flex-row-reverse' : ''}`}
          >
            {/* Single descriptive link — the whole announcement is the
                anchor text, so a standalone "Read more" never trips the
                "links do not have descriptive text" SEO/a11y audit. Points
                at /app-builder, the page the announcement is about. */}
            <Link
              href="/app-builder"
              prefetch={true}
              className={`group flex items-center gap-2 font-medium text-[#78716c] transition-colors hover:text-neutral-900 ${rtl ? 'flex-row-reverse' : ''}`}
            >
              {rtl && (
                <Arrow className="size-3 text-[#78716c] transition-transform group-hover:-translate-x-0.5" />
              )}
              <span>
                <span className="hidden sm:inline">
                  {
                    t.nav[
                      'We just closed a new funding round to build the super customer experience AI agent'
                    ]
                  }
                </span>
                <span className="sm:hidden">
                  {t.nav['New funding round closed!']}
                </span>
                {/* Literal space as well as the margin: the accessible name
                    is computed by concatenating text, and `ms-2` alone would
                    run the sentence into "Read more". */}{' '}
                <span className="ms-2 text-[#78716c] underline underline-offset-4 group-hover:text-neutral-900">
                  {t.nav['Read more']}
                </span>
              </span>
              {!rtl && (
                <Arrow className="ms-1 size-3 text-[#78716c] transition-transform group-hover:translate-x-0.5" />
              )}
            </Link>
            <button
              onClick={closeAnnouncement}
              className="absolute right-4 flex items-center justify-center rounded-full p-1 text-[#78716c]/60 transition-colors hover:bg-black/5 hover:text-[#78716c]"
              aria-label={
                locale === 'ar' ? 'إغلاق الإعلان' : 'Close announcement'
              }
            >
              <X className="size-4" />
            </button>
          </div>
        </div>
        {/* Navigation Menu */}
        <div className="container">
          <NavigationMenu
            dir={rtl ? 'rtl' : 'ltr'}
            viewport={false}
            className="min-w-full"
            delayDuration={0}
            onValueChange={(value) => setMenuOpen(!!value)}
          >
            <div className="grid w-full grid-cols-[1fr_auto_1fr] items-center gap-2 h-(--header-height)">
              <Link
                href="/"
                prefetch={true}
                aria-label={locale === 'ar' ? 'Open — الرئيسية' : 'Open — Home'}
                className="group relative flex items-center gap-2 justify-self-start"
              >
                <img
                  src={
                    useWhiteText || useDarkChrome
                      ? '/Logo Long White (1).svg'
                      : '/Logo Long Black (1).svg'
                  }
                  className="max-h-8 transition-opacity duration-300"
                  alt="Open Logo"
                />
                <span
                  className={`pointer-events-none absolute -bottom-8 ${rtl ? 'right-0' : 'left-0'} rounded-md px-3 py-1.5 text-xs font-medium whitespace-nowrap opacity-0 transition-opacity duration-200 group-hover:opacity-100 ${useWhiteText || useDarkChrome ? 'bg-white/10 text-white/90 backdrop-blur-sm' : 'bg-black/10 text-black/90 backdrop-blur-sm'}`}
                >
                  {t.nav['Automate everything™']}
                </span>
              </Link>
              <div className="flex items-center gap-2 justify-self-center xl:gap-8">
                <NavigationMenuList className="hidden gap-0 lg:flex">
                  {navigationMenuItems.map((item) => (
                    <NavigationMenuItem
                      key={item.key}
                      className={item.itemClassName}
                    >
                      {item.href ? (
                        <NavigationMenuLink asChild>
                          <Link
                            href={item.href}
                            prefetch={true}
                            className={cn(
                              navigationMenuTriggerStyle(),
                              'bg-transparent text-xs font-medium transition-colors hover:bg-white hover:text-black focus:bg-transparent xl:text-sm',
                              useWhiteText || useDarkChrome
                                ? 'text-white'
                                : 'text-foreground',
                            )}
                          >
                            {item.label}
                          </Link>
                        </NavigationMenuLink>
                      ) : item.component ? (
                        <>
                          <NavigationMenuTrigger
                            className={`bg-transparent text-xs xl:text-sm transition-colors hover:bg-white hover:text-black focus:bg-transparent data-[state=open]:bg-white data-[state=open]:text-black data-[state=open]:hover:bg-white data-[state=open]:focus:bg-white ${useWhiteText || useDarkChrome ? 'text-white' : 'text-foreground'}`}
                          >
                            {item.label}
                          </NavigationMenuTrigger>
                          <NavigationMenuContent
                            className={cn('p-6', item.contentClassName)}
                          >
                            {item.component()}
                          </NavigationMenuContent>
                        </>
                      ) : null}
                    </NavigationMenuItem>
                  ))}
                </NavigationMenuList>
              </div>
              <div className={cn('flex items-center gap-4 justify-self-end')}>
                {/* Arabic only. An English visitor has no route to /ar from
                    the chrome — that is deliberate. */}
                {locale === 'ar' && (
                  <LocaleSwitch dark={useWhiteText || useDarkChrome} />
                )}
                <Button
                  asChild
                  className={`hidden md:block ${useWhiteText ? 'bg-white text-black hover:bg-neutral-100' : 'bg-black text-white hover:bg-neutral-800'}`}
                >
                  <Link
                    href="/demo"
                    prefetch={true}
                    aria-label={
                      t.common['Learn more about Open and book a demo']
                    }
                  >
                    {t.nav['Get a demo']}
                  </Link>
                </Button>
                <a
                  href="https://platform.open.cx"
                  className={`hidden text-sm font-medium underline underline-offset-4 transition-colors md:block ${useWhiteText || useDarkChrome ? 'text-white/80 hover:text-white' : 'text-neutral-600 hover:text-neutral-900'}`}
                >
                  {t.nav['Login']}
                </a>
                <Button
                  variant="outline"
                  size="icon"
                  aria-label={
                    locale === 'ar' ? 'القائمة الرئيسية' : 'Main Menu'
                  }
                  className={`bg-transparent transition-colors lg:hidden ${useWhiteText || useDarkChrome ? 'border-white/30 text-white hover:bg-white/10' : 'border-neutral-300 bg-white text-black hover:bg-neutral-100'}`}
                  onClick={() => {
                    if (open) {
                      setOpen(false);
                      setSubmenu(null);
                    } else {
                      setOpen(true);
                    }
                  }}
                >
                  {!open && <Menu className="size-4" />}
                  {open && <X className="size-4" />}
                </Button>
              </div>
            </div>

            {/* Mobile Menu */}
            {open && (
              <div className="border-border bg-background fixed inset-0 top-[72px] container flex h-[calc(100vh-72px)] w-full flex-col overflow-auto border-t lg:hidden">
                {submenu && (
                  <div className="mt-3">
                    <Button
                      variant="link"
                      onClick={() => setSubmenu(null)}
                      className={`relative flex items-center gap-2 ${rtl ? '-right-4 flex-row-reverse' : '-left-4'}`}
                    >
                      {rtl ? (
                        <ArrowRight className="size-4 text-xs" />
                      ) : (
                        <ArrowLeft className="size-4 text-xs" />
                      )}
                      {t.nav['Go back']}
                    </Button>
                  </div>
                )}
                {submenu === null && (
                  <div>
                    {navigationMenuItems.map((item) =>
                      item.href ? (
                        <Link
                          key={item.key}
                          href={item.href}
                          prefetch={true}
                          onClick={handleCloseMenu}
                          className={`border-border flex w-full items-center border-b py-6 text-start ${rtl ? 'flex-row-reverse' : ''}`}
                        >
                          <span className="flex-1 text-sm font-medium">
                            {item.label}
                          </span>
                          <span className="shrink-0">
                            <Arrow className="size-4" />
                          </span>
                        </Link>
                      ) : (
                        <button
                          key={item.key}
                          type="button"
                          className={`border-border flex w-full items-center border-b py-6 text-start ${rtl ? 'flex-row-reverse' : ''}`}
                          onClick={() => setSubmenu(item.key)}
                        >
                          <span className="flex-1 text-sm font-medium">
                            {item.label}
                          </span>
                          <span className="shrink-0">
                            <Arrow className="size-4" />
                          </span>
                        </button>
                      ),
                    )}
                  </div>
                )}
                {navigationMenuItems.map(
                  (item) =>
                    submenu === item.key &&
                    item.component && (
                      <div key={item.key}>
                        <h2 className="pt-4 pb-6 text-start text-lg">
                          {item.label}
                        </h2>
                        {item.component(handleCloseMenu)}
                      </div>
                    ),
                )}
                {/* Mobile menu footer */}
                <div className="mx-[2rem] mt-auto flex flex-col items-center gap-8 py-24">
                  <a
                    href="https://platform.open.cx"
                    className="text-sm font-medium text-neutral-600 underline underline-offset-4 hover:text-neutral-900"
                  >
                    {t.nav['Login']}
                  </a>
                </div>
              </div>
            )}
          </NavigationMenu>
        </div>
      </section>
    </>
  );
};

export default Navbar;
